import { db } from "$lib/firebase";
import { doc, getDoc, type DocumentData, updateDoc, query, collection, where, 
    getDocs, QueryDocumentSnapshot, setDoc, arrayUnion, DocumentReference } from "firebase/firestore";
import { getPictureURL } from "./shopImage";

let CACHE_SHOP: { [key: string]: Shop } = {};

export function invalidateShopCache() {
    CACHE_SHOP = {};
}

export async function getShopByName(shopID:string): Promise<Shop> {
    if (typeof CACHE_SHOP[shopID] !== "undefined") {
        return CACHE_SHOP[shopID];
    }

    let shop:Shop = generateShop();
    // console.log(shopID);
    try {
        const shopRef = doc(db, "shops", shopID);
        const shopSnap = await getDoc(shopRef);
    
        if (shopSnap.exists()) {    
            shop = await convertShop(shopSnap);            
        } 
    } catch (e) {
        console.log(e)
    }

    CACHE_SHOP[shopID] = shop;
    return shop
}

async function convertShop(snap: QueryDocumentSnapshot):Promise<Shop> {
    const data = snap.data();

    const shop: Shop = {
        id: snap.id,
        displayName: data?.displayName ?? "",
        address: data?.address ?? "",
        kitchen_uid: data?.kitchen_uid ?? "",
        owner_uid: data?.owner_uid ?? "",
        // hours: convertHours(data.hours ?? []),
        open: data?.open ?? false,
        paymentTypes: data?.paymentTypes ?? [],
        messageTypes: data?.messageTypes ?? [],
        colours: convertColours(data?.colours ?? {}),
        displayDescription: data?.displayDescription ?? "",
        displayLocation: data?.displayLocation ?? "",
        displayOrder: data?.displayOrder ?? 0,
        imageURL: await getPictureURL(data?.owner_uid, snap.id) ?? "",
        displaySubtitle: data?.displaySubtitle ?? "",
        customerNameMsg: data?.customerNameMsg ?? "",
    }

    return shop;
}

export function generateShop(): Shop {
    const shop:Shop = {
        id: "The Unknown Shop",
        displayName: "The Unknown Shop",
        kitchen_uid: "Using ghostfire",
        owner_uid: "Owned by gazorpazorpfield",  
        // hours: defaultHours, 
        open: false,
        paymentTypes: [],
        messageTypes: [],
        colours: convertColours({}),
        displayDescription: "seeDesc",
        displayLocation: "seeLoc",
        displayOrder: 0,
        imageURL: "",
        displaySubtitle: "seeSub",
        customerNameMsg: "seeName",
    }

    return shop
}

export async function getShopsOfOwner(owner_uid: string): Promise<Shop[]> {
    const shops:Shop[] = [];

    const q = query(collection(db, "shops"), where("owner_uid", "==", owner_uid));

    const querySnapshot = await getDocs(q);
    
    for (const docSnap of querySnapshot.docs) {
        const shop:Shop = await convertShop(docSnap);
        shops.push(shop);
    }

    return shops;
}

export async function getOwnerByShopName(shopID: string): Promise<string> {
    const docRef = doc(db, "shops", shopID);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
        return docSnap.data().owner_uid ?? "";
    } else {
        return "";
    }
}

export async function saveShop(shop: Shop): Promise<void> {
    const docRef = doc(db, "shops", shop.id);

    // console.log(shop.imageURL);

    await updateDoc(docRef, shop);
}

export function convertColours(data: DocumentData): Colours {
    return {
        primary: data.primary ?? "#0096FF",
        accent: data.accent ?? "#90EE90",
        neutral: data.neutral ?? "#ffffff",
    }
}

export async function migrateDish(fromDish: QueryDocumentSnapshot, toDish: DocumentReference): Promise<string> {
    const docSnapTo = await getDoc(toDish);

    if (docSnapTo.exists()) {
        // console.log("Already exists: " + docSnapTo.id);
        return "Already exists: " + docSnapTo.id;
    } 

    await setDoc(toDish, fromDish.data());
    return "";
}

export async function migrateMenu(fromShop: string, toShop: string): Promise<string[]> {
    let alreadyExists: Array<Promise<string>> = [];

    const menuFrom = collection(db, "shops", fromShop, "menu");
    const menuTo = collection(db, "shops", toShop, "menu");

    const querySnapshot = await getDocs(menuFrom);

    for (const docSnap of querySnapshot.docs) {
        alreadyExists.push(migrateDish(docSnap, doc(menuTo, docSnap.id)));
    }

    const categoriesTo = doc(db, "shops", toShop, "categories", "categories");
    const categoriesFrom = doc(db, "shops", fromShop, "categories", "categories");

    const categoriesFromSnap = await getDoc(categoriesFrom);
    const snapDataCategories = categoriesFromSnap.data()?.categories;
    // console.log(snapDataCategories);

    // add categories from fromShop to toShop
    await updateDoc(categoriesTo, {categories: arrayUnion(...snapDataCategories)});

    return await Promise.all(alreadyExists);
}

export async function openStore(shopID: string): Promise<void> {
    const docRef = doc(db, "shops", shopID);

    await updateDoc(docRef, {open: true});
}

export async function closeStore(shopID: string): Promise<void> {
    const docRef = doc(db, "shops", shopID);

    await updateDoc(docRef, {open: false});
}

export function convertNumberTime(num: number): OpTime {
    const h = Math.floor(num);
    const m = (num - Math.floor(num)) * 60;

    const newTime: OpTime = {
        hour: h,
        minute: m
    }

    return newTime
}

export function showOpTime(time: OpTime): string {
    return time.hour.toString().padStart(2, '0') + ":" + time.minute.toString().padStart(2, '0');
}
